/* eslint-disable import/no-internal-modules */
import { unlinkAgent } from '#~/agent/components/monitoring/wiring.js';
import { clearOnboardingResume } from '#~/onboarding/resume.js';
import { useAgents } from '#~/agent/hooks.js';
import { useAuth0 } from '#~/auth/index.js';
import { useCalibrate } from '#~/control/components/hooks.js';
import { reorder } from '#~/device/commands/index.js';
import { useRemove, useUpdate } from '#~/device/components/hooks.js';
import { removeTransitoryCamera } from '#~/device/commands/pending.js';
import { getTransitoryDevice, toSyntheticDevice } from '#~/device/transitory.js';
import { raiseError } from '#~/notifications/index.js';
import { unregisterMultiAddOrigin } from '#~/notifications/multi-add-origins.js';
import { useSelectedVideo } from '#~/room/hooks.js';
import type { Room } from '#~/room/index.js';
import { locateDevice } from '#~/room/index.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { resolveDeviceAnalyticsContext } from '#~/analytics/device-context.js';
import { usePrompt } from '@studio/design-system/prompt';
import { usePendingDeviceIds, type Device } from '@studio/models/device';
import { useLatestFn } from '@studio/react-utils/hooks';
import { useMemo } from 'react';
import { useAddVideo, useSettings } from '../hooks.js';
import type { Wiring } from './types.js';
import { createSettingsLifecycleTracker } from '#~/analytics/domains/index.js';
// ENG-4406: the placeholder settings deep-link targets the Iris-Connection
// panel's Authentication tab — same `Panel` enum the chip click uses
// (chip-click.ts). Per `reference-rules-in-comments` (ai.knowledge/global).
import { Panel } from '#~/video/components/settings/store/index.js';

/** @internal */
export const useWiring = (devices: Room['devices']): Wiring => {
  const { getAccessTokenSilently } = useAuth0();

  const [selectedId, handleSelect] = useSelectedVideo();
  const handleAdd = useAddVideo();
  const calibrate = useCalibrate();
  const handleUpdate = useUpdate();
  const remove = useRemove({ type: 'video' });
  const settings = useSettings();
  const prompt = usePrompt();
  const { track } = useAnalytics();
  const settingsLifecycleTracker = useMemo(() => createSettingsLifecycleTracker(track), [track]);

  const handleCalibrate = useLatestFn((deviceId: Device['id']) => {
    calibrate(deviceId);
  });

  const handleRemove = useLatestFn((deviceId: Device['id']) => {
    // A user removal ends the post-add remediation window (PRD §9.4): drop the
    // device's multi-add origin so the observer's orphan-cleanup pass clears any
    // lingering config toast (combined / specific / Authenticate). Runs for BOTH
    // the transitory-placeholder branch and the real-device branch below; a
    // no-op for non-multi-add devices.
    unregisterMultiAddOrigin(deviceId.toString());

    if (getTransitoryDevice(deviceId)) {
      // A pending camera: drop its tile and the record its bridge persists.
      void removeTransitoryCamera(deviceId, () => getAccessTokenSilently());
      return;
    }

    const toBeRemoved = devices[deviceId];

    if (toBeRemoved) {
      remove(toBeRemoved);
    }
  });

  const handleRename = useLatestFn(async (deviceId: Device['id'], nextName: string) => {
    const token = await getAccessTokenSilently();

    handleUpdate(deviceId, { name: nextName }, token).catch((error) =>
      raiseError(error, { actionable: true })
    );
  });

  const handleReorder = useLatestFn(async (deviceIds: Device['id'][]) => {
    const token = await getAccessTokenSilently();

    reorder(deviceIds, devices, token).catch((error) => raiseError(error, { actionable: true }));
  });

  // Optional `options` carries deep-link targets (`defaultPanel` +
  // `defaultTab`) from the per-tile chip click. When omitted (context
  // menu / existing callers), the call is byte-identical to the
  // previous behavior.
  const handleSettings = useLatestFn(
    (deviceId: Device['id'], options?: Parameters<typeof settings>[1]) => {
      // ENG-4406: a transitory credential placeholder is never committed to
      // `room.devices`, so the `devices[deviceId]` lookup below misses and the
      // gear/menu click would no-op. Resolve it here FIRST, byte-equivalent to
      // the canvas 'Authenticate' chip path (chip-click.ts): synthesize the
      // placeholder device and open Advanced Settings deep-linked to the
      // Authentication tab. `defaultTab: 'authentication'` is REQUIRED, not
      // cosmetic — it arms the ENG-4402 anti-eviction guard from first paint
      // (AuthenticationContent mount → setTransitoryAuthTabOpen), exactly like
      // the chip. A caller-supplied `options` (the chip) still wins.
      // Per `reference-rules-in-comments` (ai.knowledge/global).
      const placeholder = getTransitoryDevice(deviceId);

      if (placeholder) {
        settingsLifecycleTracker.trackAdvancedSettingsOpened({
          surface: 'video_sources_tray',
          device_id: deviceId
        });
        settings(
          toSyntheticDevice(placeholder),
          options ?? { defaultPanel: Panel.IrisConnection, defaultTab: 'authentication' }
        );
        return;
      }

      const device = devices[deviceId];

      if (device) {
        // Advanced-settings open analytics migrated to the domain tracker
        // (main), enriched with resolveDeviceAnalyticsContext. HEAD's deep-link
        // `options` (defaultPanel + defaultTab from the per-tile chip click,
        // Multi-Add Phase 2) is preserved on the `settings(...)` call.
        // Per `stay-in-scope` (ai.knowledge/global).
        settingsLifecycleTracker.trackAdvancedSettingsOpened({
          ...(resolveDeviceAnalyticsContext(deviceId) ?? { device_id: deviceId }),
          surface: 'video_sources_tray'
        });
        settings(device, options);
      }
    }
  );

  const agents = useAgents((a) => a);

  const handleUnlink = useLatestFn(async (deviceId: Device['id']) => {
    const token = await getAccessTokenSilently();

    const pendingDevices = usePendingDeviceIds();

    const device =
      locateDevice(deviceId) ?? pendingDevices[deviceId] ?? ({} as Record<string, any>);
    const agent = agents.find((a) => a.id === device?.agentId);

    if (!agent) {
      return;
    }

    unlinkAgent(prompt, agent, token, () => {
      // The reload would otherwise reopen the onboarding camera screen from a
      // note written in the last two minutes.
      clearOnboardingResume();
      window.location.reload();
    });
  });

  return {
    handleAdd,
    handleCalibrate,
    handleRemove,
    handleRename,
    handleReorder,
    handleSelect,
    handleSettings,
    handleUnlink,
    selectedId
  };
};
